import {
  Alert,
  Button,
  Card,
  CardBody,
  EmptyState,
  formatDate,
  Money,
  SummaryStat,
  SummaryStats,
} from '@hkdevs/design-system';
import { useState } from 'react';
import { usePending } from '../../api/queries/pay';
import { dollars, LineItemList, LineItemsLoading } from '../lineItems/LineItemList';

/** Pending pay's URL state: the line-item search. */
export interface PendingPaySearch {
  q?: string | undefined;
}

export function parsePendingPaySearch(search: Record<string, unknown>): PendingPaySearch {
  return { q: typeof search.q === 'string' && search.q ? search.q : undefined };
}

export interface PendingPayViewProps {
  /** A supervised rep's pending pay; the caller's own when left out. */
  repId?: string;
  search: PendingPaySearch;
  onSearchChange: (next: PendingPaySearch) => void;
}

/**
 * A rep's pending pay (pc-dbb.5): the one upcoming pay date and its total (danger when
 * negative), and the pending line items with the same search and expansion as a check.
 */
export function PendingPayView({ repId, search, onSearchChange }: PendingPayViewProps) {
  const pending = usePending(repId);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (pending.isPending) {
    return <LineItemsLoading dateLabel="Upcoming pay date" totalLabel="Total pending" />;
  }
  if (pending.isError) {
    return (
      <Alert
        tone="danger"
        title="Pending pay couldn’t be loaded"
        action={
          <Button size="sm" variant="secondary" onClick={() => void pending.refetch()}>
            Retry
          </Button>
        }
      >
        Check your connection and try again.
      </Alert>
    );
  }
  const { payDate, totalCents, lineItems, columns } = pending.data;
  if (!payDate || lineItems.length === 0) {
    return (
      <Card>
        <CardBody>
          <EmptyState
            title="Nothing pending"
            description="Nothing pending for the upcoming pay date."
            icon="credit-card"
            headingLevel={2}
          />
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      <SummaryStats aria-label="Pending summary">
        <SummaryStat label="Upcoming pay date" value={formatDate(payDate)} size="lg" />
        <SummaryStat
          label="Total pending"
          value={<Money value={dollars(totalCents)} />}
          size="lg"
        />
      </SummaryStats>
      <LineItemList
        lineItems={lineItems}
        totalCents={totalCents}
        totalLabel="Total pending"
        columns={columns}
        query={search.q ?? ''}
        onQueryChange={(q) => onSearchChange({ q: q || undefined })}
        expandedId={expandedId}
        onExpandedChange={setExpandedId}
      />
    </>
  );
}
